import type { ReactElement } from 'react'
import type { ScanId } from '../live/types.js'
import { useDemoStore, type ModelLabelFindings, type CountertopFindings, type UndersinkFindings } from '../state/store.js'

interface Chip { key: string; text: string }

/**
 * Per-scan formatter: takes whatever fields the AI has streamed so far via
 * `update_findings` and turns each into a short chip. Skips undefined fields
 * (so chips appear progressively) and internal-only fields like confidence.
 */
const formatModelLabel = (p: Partial<ModelLabelFindings>): Chip[] => {
  const chips: Chip[] = []
  if (p.brand) chips.push({ key: 'brand', text: p.brand })
  if (p.model) chips.push({ key: 'model', text: p.model })
  return chips
}

const formatCountertop = (p: Partial<CountertopFindings>): Chip[] => {
  const chips: Chip[] = []
  if (p.material) chips.push({ key: 'material', text: p.material })
  if (typeof p.overhangInches === 'number') chips.push({ key: 'overhang', text: `${p.overhangInches}″` })
  return chips
}

const UNDERSINK_VALVE_LABEL: Record<NonNullable<UndersinkFindings['valve']>, string> = {
  'quarter-turn': 'Quarter-turn valve',
  'gate': 'Gate valve',
  'compression': 'Compression valve',
  'none-visible': 'No valve seen',
}
const UNDERSINK_DRAIN_LABEL: Record<NonNullable<UndersinkFindings['drain']>, string> = {
  'standard-hose': 'Standard drain',
  'garbage-disposal': 'Disposal drain',
  'none-visible': 'No drain seen',
}

const formatUndersink = (p: Partial<UndersinkFindings>): Chip[] => {
  const chips: Chip[] = []
  if (p.valve) chips.push({ key: 'valve', text: UNDERSINK_VALVE_LABEL[p.valve] })
  if (p.drain) chips.push({ key: 'drain', text: UNDERSINK_DRAIN_LABEL[p.drain] })
  if (p.outlet) {
    chips.push({
      key: 'outlet',
      text: p.outlet.present ? `Outlet: ${p.outlet.side}` : 'No outlet',
    })
  }
  if (typeof p.drainLoopInches === 'number') chips.push({ key: 'loop', text: `Loop ${p.drainLoopInches}″` })
  return chips
}

const formatters = {
  'model-label': formatModelLabel,
  'countertop': formatCountertop,
  'undersink': formatUndersink,
} as const

interface Props {
  scan: ScanId
}

export const LiveFindingsChips = ({ scan }: Props): ReactElement | null => {
  const { state } = useDemoStore()
  const patch = state.livePatches[scan]
  if (!patch) return null
  const chips = scan === 'model-label'
    ? formatters['model-label'](patch as Partial<ModelLabelFindings>)
    : scan === 'countertop'
      ? formatters.countertop(patch as Partial<CountertopFindings>)
      : formatters.undersink(patch as Partial<UndersinkFindings>)
  if (chips.length === 0) return null

  return (
    <div className="flex flex-wrap justify-center gap-2 mt-2">
      {chips.map(c => (
        <span
          key={c.key}
          className="inline-block bg-foreground/70 backdrop-blur-md text-background text-xs px-3 py-1 rounded-full transition-opacity duration-200"
        >
          {c.text}
        </span>
      ))}
    </div>
  )
}
